<template>
  <div class="card border border-dashed border-gray-300">
    <div class="card-body p-4">
      <div class="d-flex align-items-center justify-content-between mb-3">
        <div>
          <div class="fw-bold">Itens</div>
          <div class="text-muted fs-7">Busque no catálogo ou cadastre um item rápido</div>
        </div>
        <button class="btn btn-sm btn-light-primary" type="button" @click="showNewItem = !showNewItem">
          <i class="bi bi-plus-lg me-1"></i>Novo item
        </button>
      </div>

      <div class="row g-3 align-items-end">
        <div class="col-md-7">
          <label class="form-label">Item do catálogo</label>
          <InputSearchCatalogItem v-model="selectedItemId"
                                  :display-value="selectedItemLabel"
                                  :item-type="budgetType"
                                  @select="onCatalogItemSelect"
                                  @clear="selectedItemLabel = ''"/>
        </div>
        <div class="col-md-2">
          <label class="form-label">Valor unit.</label>
          <input class="form-control form-control-solid" type="number" min="0.01" step="0.01" v-model.number="itemValue">
        </div>
        <div class="col-md-1">
          <label class="form-label">Qtde.</label>
          <input class="form-control form-control-solid" type="number" min="1" step="1" v-model.number="itemQuantity">
        </div>
        <div class="col-md-2">
          <button class="btn btn-primary w-100" type="button" @click="addItem">
            Adicionar
          </button>
        </div>
      </div>

      <div class="bg-light rounded p-3 mt-4" v-if="showNewItem">
        <div class="row g-3 align-items-end">
          <div class="col-md-7">
            <label class="form-label required">Nome do item</label>
            <input class="form-control form-control-solid" v-model="newItem.item_name">
          </div>
          <div class="col-md-3">
            <label class="form-label required">Valor</label>
            <input class="form-control form-control-solid" type="number" min="0.01" step="0.01" v-model.number="newItem.value">
          </div>
          <div class="col-md-2">
            <button class="btn btn-light-primary w-100" type="button" @click="createItem">
              Criar
            </button>
          </div>
          <div class="col-12">
            <label class="form-label">Observação</label>
            <textarea class="form-control form-control-solid" rows="2" v-model="newItem.item_obs"></textarea>
          </div>
        </div>
      </div>

      <div class="table-responsive mt-4" v-if="modelValue.length">
        <table class="table table-row-bordered align-middle mb-0">
          <thead>
          <tr class="fw-bold text-muted">
            <th>Item</th>
            <th class="w-100px text-center">Qtde.</th>
            <th class="w-150px">Valor unit.</th>
            <th class="w-150px">Total</th>
            <th class="w-80px text-center">Ações</th>
          </tr>
          </thead>
          <tbody>
          <tr v-for="(item, index) in modelValue" :key="`${item.id_item}-${index}`">
            <td>{{ item.description }}</td>
            <td class="text-center">
              <input class="form-control form-control-sm form-control-solid text-center"
                     type="number" min="1" step="1"
                     :value="item.item_quantity ?? 1"
                     @input="changeQuantity(index, $event.target.value)">
            </td>
            <td>R$ {{ Number(item.item_value).toFixed(2) }}</td>
            <td class="fw-semibold">R$ {{ lineTotal(item).toFixed(2) }}</td>
            <td class="text-center">
              <button class="btn btn-sm btn-icon btn-light-danger" type="button" @click="removeItem(index)">
                <i class="bi bi-trash"></i>
              </button>
            </td>
          </tr>
          </tbody>
        </table>
      </div>

      <div class="notice bg-light-warning rounded border-warning border border-dashed p-4 mt-4" v-else>
        Adicione pelo menos um item ao orçamento.
      </div>
    </div>
  </div>
</template>

<script>
import http from "@/helpers/http";
import Alert from "@/helpers/alert";
import InputSearchCatalogItem from "@/components/InputSearchCatalogItem.vue";

export default {
  name: "BudgetItemsEditor",
  components: { InputSearchCatalogItem },
  props: ['modelValue', 'budgetType'],
  emits: ['update:modelValue'],
  data() {
    return {
      selectedItemId: null,
      selectedItemLabel: '',
      itemValue: null,
      itemQuantity: 1,
      showNewItem: false,
      newItem: {
        item_name: '',
        item_obs: '',
        value: null,
      },
    }
  },
  computed: {
    place() {
      return this.$store.state.auth.place;
    },
  },
  watch: {
    budgetType() {
      this.selectedItemId = null;
      this.selectedItemLabel = '';
      this.itemValue = null;
    },
  },
  methods: {
    itemLabel(item) {
      const exam = item.exam;
      if (exam) return [exam.exam_tuss_code, exam.exam_name].filter(Boolean).join(' - ');
      return item.item_name;
    },
    onCatalogItemSelect(item) {
      this.selectedItemLabel = this.itemLabel(item);
      this.fillValue(item);
    },
    fillValue(item) {
      this.itemValue = Number(item?.active_value?.item_value ?? 0);
    },
    addItem() {
      if (!this.selectedItemId || !this.itemValue) return;
      this.$emit('update:modelValue', [
        ...this.modelValue,
        {
          id_item: this.selectedItemId,
          description: this.selectedItemLabel,
          item_value: Number(this.itemValue),
          item_quantity: Math.max(1, Number(this.itemQuantity) || 1),
        }
      ]);
      this.selectedItemId = null;
      this.selectedItemLabel = '';
      this.itemValue = null;
      this.itemQuantity = 1;
    },
    lineTotal(item) {
      return Number(item.item_value || 0) * Math.max(1, Number(item.item_quantity ?? 1));
    },
    changeQuantity(index, value) {
      const quantity = Math.max(1, Number(value) || 1);
      this.$emit('update:modelValue', this.modelValue.map((item, i) => (
          i === index ? {...item, item_quantity: quantity} : item
      )));
    },
    removeItem(index) {
      this.$emit('update:modelValue', this.modelValue.filter((_, i) => i !== index));
    },
    createItem() {
      if (!this.newItem.item_name || !this.newItem.value || !this.budgetType) return;
      http.post(`/places/${this.place.id_place}/items`, {
        item_name: this.newItem.item_name,
        item_obs: this.newItem.item_obs,
        item_type: this.budgetType,
        item_active: true,
        item_require_sched: false,
        active_value: {
          item_base_date: new Date().toISOString().substring(0, 10),
          item_value: Number(this.newItem.value),
        },
      }).then(response => {
        this.onCatalogItemSelect(response.data);
        this.newItem = {item_name: '', item_obs: '', value: null};
        this.showNewItem = false;
        Alert.toastSuccess('Item criado com sucesso!');
      }).catch(e => Alert.error(e.response?.data?.message ?? e.response?.data));
    },
  },
}
</script>
